import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import {
  toArchitectureTemplate,
  toComponentLink,
  toProjectComponent,
  toProjectRepository,
} from '@/shared/api/adapt'
import { api } from '@/shared/api/client'
import type {
  ArchitectureRead,
  ArchitectureTemplateRead,
  ComponentLinkRead,
  ComponentRead,
  RepositoryRead,
} from '@/shared/api/contracts'
import { qk } from '@/shared/api/query-keys'
import { errorMessage } from '@/shared/components/data'
import { useT } from '@/shared/i18n'

import type { ComponentPosition, ProjectComponent } from './types'

export function useComponents(projectId: string) {
  return useQuery({
    queryKey: qk.projects.components(projectId),
    queryFn: async () =>
      (await api.get<ComponentRead[]>(`projects/${projectId}/components`)).map((component) =>
        toProjectComponent(component),
      ),
    enabled: Boolean(projectId),
    // El diagrama es una pantalla de lectura larga: un refetch al volver a la
    // pestaña reacomodaría nodos debajo del cursor.
    refetchOnWindowFocus: false,
  })
}

export function useComponentLinks(projectId: string) {
  return useQuery({
    queryKey: qk.projects.componentLinks(projectId),
    queryFn: async () =>
      (await api.get<ComponentLinkRead[]>(`projects/${projectId}/component-links`)).map(
        toComponentLink,
      ),
    enabled: Boolean(projectId),
    refetchOnWindowFocus: false,
  })
}

export function useRepositories(projectId: string) {
  return useQuery({
    queryKey: qk.projects.repositories(projectId),
    queryFn: async () =>
      (await api.get<RepositoryRead[]>(`projects/${projectId}/repositories`)).map(
        toProjectRepository,
      ),
    enabled: Boolean(projectId),
  })
}

export function useArchitectureTemplates() {
  return useQuery({
    queryKey: qk.architecture.templates(),
    queryFn: async () =>
      (await api.get<ArchitectureTemplateRead[]>('architecture-templates')).map(
        toArchitectureTemplate,
      ),
    staleTime: 60 * 60_000,
  })
}

/**
 * `PUT /projects/{id}/components/positions` — el layout de un tirón.
 *
 * Se guarda al soltar, no en cada píxel: arrastrar un nodo no debería producir
 * cuarenta requests. Optimista sobre la cache porque el nodo ya está donde lo
 * soltaste; si falla, el refetch lo devuelve a su lugar y el toast lo explica.
 */
export function useSavePositions(projectId: string) {
  const client = useQueryClient()
  const t = useT()

  return useMutation({
    // El backend recibe un OBJETO con `positions`, no un array pelado, y cada
    // entrada se llama `component_id`, no `id`. Mandarlo como estaba daba 422.
    mutationFn: async (positions: ComponentPosition[]) =>
      (
        await api.put<ComponentRead[]>(`projects/${projectId}/components/positions`, {
          positions: positions.map((position) => ({
            component_id: position.id,
            position_x: position.position_x ?? 0,
            position_y: position.position_y ?? 0,
          })),
        })
      ).map((component) => toProjectComponent(component)),
    onMutate: async (positions) => {
      await client.cancelQueries({ queryKey: qk.projects.components(projectId) })
      const previous = client.getQueryData<ProjectComponent[]>(qk.projects.components(projectId))

      if (previous) {
        const byId = new Map(positions.map((position) => [position.id, position]))
        client.setQueryData<ProjectComponent[]>(
          qk.projects.components(projectId),
          previous.map((component) => {
            const next = byId.get(component.id)
            return next
              ? { ...component, position_x: next.position_x, position_y: next.position_y }
              : component
          }),
        )
      }

      return { previous }
    },
    onError: (error, _positions, context) => {
      if (context?.previous) {
        client.setQueryData(qk.projects.components(projectId), context.previous)
      }
      toast.error(errorMessage(error, t('architecture.error.savePositions')))
    },
  })
}

export function useApplyTemplate(projectId: string) {
  const client = useQueryClient()
  const t = useT()

  return useMutation({
    // La ruta real es `.../architecture/apply-template` y devuelve la
    // arquitectura ENTERA (componentes + links + repos), no solo los
    // componentes nuevos.
    mutationFn: (templateKey: string) =>
      api.post<ArchitectureRead>(`projects/${projectId}/architecture/apply-template`, {
        template_key: templateKey,
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: qk.projects.components(projectId) })
      void client.invalidateQueries({ queryKey: qk.projects.componentLinks(projectId) })
    },
    onError: (error) => {
      toast.error(errorMessage(error, t('architecture.error.applyTemplate')))
    },
  })
}

export function useSyncRepository(projectId: string) {
  const client = useQueryClient()
  const t = useT()

  return useMutation({
    mutationFn: async (repositoryId: string) =>
      toProjectRepository(await api.post<RepositoryRead>(`repositories/${repositoryId}/sync`)),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: qk.projects.repositories(projectId) })
    },
    onError: (error) => {
      toast.error(errorMessage(error, t('architecture.error.syncRepository')))
    },
  })
}

/** Los roles que el backend acepta para un repo (`RepositoryCreate.role`). */
export const REPOSITORY_ROLES = [
  'frontend',
  'backend',
  'infra',
  'mobile',
  'shared',
  'docs',
] as const

export type RepositoryRole = (typeof REPOSITORY_ROLES)[number]

export interface RepositoryCreateInput {
  url: string
  role: RepositoryRole
  owner?: string | null
  name?: string | null
  default_branch?: string | null
}

/**
 * Vincular un repositorio a un proyecto.
 *
 * `owner` y `name` son opcionales porque el backend los deduce de la URL; van
 * en el contrato igual para el día en que alguien pegue una URL que no siga el
 * formato de GitHub.
 *
 * Sin token de GitHub el repo se guarda lo mismo, sin último commit ni PRs
 * (`docs/estado.md`): el alta no puede depender de una integración que puede no
 * estar configurada.
 */
export function useCreateRepository(projectId: string) {
  const client = useQueryClient()
  const t = useT()

  return useMutation({
    mutationFn: async (input: RepositoryCreateInput) =>
      toProjectRepository(
        await api.post<RepositoryRead>(`projects/${projectId}/repositories`, { ...input }),
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: qk.projects.repositories(projectId) })
    },
    onError: (error) => {
      toast.error(errorMessage(error, t('architecture.error.createRepository')))
    },
  })
}
